// app/src/lib/scale/subscription.js — client wrapper for the subscription API.
//
// getStatus() reads the authoritative billing state (planTier → lifecycle
// status). createSubscription() posts a Finix hosted-tokenization token to the
// server, which creates the Identity/Instrument/Subscription. Card data never
// touches this code — only the one-time token from Finix.js.

import { getConfig } from '../config.js';
import { getAccessToken } from '../auth/cognitoClient.js';

/** GET /subscription/status → { status, tier, cycle, trialEndsAt, currentPeriodEnd, ... } */
export async function getStatus() {
  const cfg = getConfig();
  const at = await getAccessToken();
  if (!at) throw new Error('subscription: not signed in');
  const res = await fetch(`${cfg.apiBaseUrl}/subscription/status`, {
    headers: { authorization: `Bearer ${at}` },
  });
  if (!res.ok) throw new Error(`subscription/status ${res.status}`);
  return res.json();
}

/**
 * POST /subscription/create with a Finix token. On a declined card / unavailable
 * plan the server returns 402; we surface `error`/`reason` for the UI.
 * `mode:'trial'` stores the card + plan with NO charge — the sweep charges when
 * the 30-day trial ends; default ('charge') charges the first period now.
 * `billingCity`/`billingZip` feed the Illinois city-of-Chicago gate server-side.
 * `tosVersion`/`tosAcceptedAt` record the Terms-of-Service acceptance the UI
 * collected (the server may persist them; unknown fields are ignored today).
 * @param {{ token:string, email:string, firstName?:string, lastName?:string, tier:string, cycle:string, billingState?:string, billingCity?:string, billingZip?:string, mode?:'trial'|'charge', tosVersion?:string, tosAcceptedAt?:string }} args
 */
export async function createSubscription(args) {
  const cfg = getConfig();
  const at = await getAccessToken();
  if (!at) throw new Error('subscription: not signed in');
  const res = await fetch(`${cfg.apiBaseUrl}/subscription/create`, {
    method: 'POST',
    headers: { authorization: `Bearer ${at}`, 'content-type': 'application/json' },
    body: JSON.stringify({
      token: args.token,
      email: args.email,
      firstName: args.firstName,
      lastName: args.lastName,
      tier: args.tier,
      cycle: args.cycle,
      billingState: args.billingState,
      billingCity: args.billingCity,
      billingZip: args.billingZip,
      mode: args.mode || 'charge',
      tosVersion: args.tosVersion,
      tosAcceptedAt: args.tosAcceptedAt,
    }),
  });
  let body = {};
  try { body = await res.json(); } catch { /* non-JSON */ }
  if (!res.ok) {
    const e = new Error(body.error || `subscription/create ${res.status}`);
    e.status = res.status;
    e.reason = body.reason || null;
    throw e;
  }
  return body;
}

/** POST /subscription/update-card — replace the card on the active subscription. */
export async function updateCard({ token }) {
  const cfg = getConfig();
  const at = await getAccessToken();
  if (!at) throw new Error('subscription: not signed in');
  const res = await fetch(`${cfg.apiBaseUrl}/subscription/update-card`, {
    method: 'POST',
    headers: { authorization: `Bearer ${at}`, 'content-type': 'application/json' },
    body: JSON.stringify({ token }),
  });
  let body = {};
  try { body = await res.json(); } catch { /* non-JSON */ }
  if (!res.ok) { const e = new Error(body.error || `update-card ${res.status}`); e.status = res.status; throw e; }
  return body;
}

/** POST /subscription/auto-renew { enabled } — toggle renewal; returns { autoRenew }. */
export async function setAutoRenew(enabled) {
  const cfg = getConfig();
  const at = await getAccessToken();
  if (!at) throw new Error('subscription: not signed in');
  const res = await fetch(`${cfg.apiBaseUrl}/subscription/auto-renew`, {
    method: 'POST',
    headers: { authorization: `Bearer ${at}`, 'content-type': 'application/json' },
    body: JSON.stringify({ enabled: !!enabled }),
  });
  let body = {};
  try { body = await res.json(); } catch { /* non-JSON */ }
  if (!res.ok) { const e = new Error(body.error || `auto-renew ${res.status}`); e.status = res.status; throw e; }
  return body;
}

/** POST /subscription/cancel — cancel; returns { status, accessUntil }. */
export async function cancelSubscription() {
  const cfg = getConfig();
  const at = await getAccessToken();
  if (!at) throw new Error('subscription: not signed in');
  const res = await fetch(`${cfg.apiBaseUrl}/subscription/cancel`, {
    method: 'POST',
    headers: { authorization: `Bearer ${at}` },
  });
  let body = {};
  try { body = await res.json(); } catch { /* non-JSON */ }
  if (!res.ok) { const e = new Error(body.error || `cancel ${res.status}`); e.status = res.status; throw e; }
  return body;
}
